import React from 'react';

export type PenStatus = 'active' | 'empty' | 'expired' | 'retired';

// DOS-5: one visual language for pen status everywhere —
// green = in use, amber = used up / lapsed, grey = manually shelved.
// DOS-16: `help` is surfaced as a tooltip so users can learn what each status means.
const STATUS_STYLES: Record<PenStatus, { label: string; badge: string; dot: string; help: string }> = {
  active: { label: 'Active', badge: 'bg-emerald-100 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500', help: 'In use — you can log doses to this pen.' },
  empty: { label: 'Empty', badge: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500', help: 'No clicks remaining. History and cost savings are kept.' },
  expired: { label: 'Expired', badge: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500', help: 'Past its in-use window (measured from the first dose).' },
  retired: { label: 'Retired', badge: 'bg-stone-100 text-stone-600 border-stone-300', dot: 'bg-stone-400', help: 'Manually archived. Data is preserved — you can restore it anytime.' },
};

interface PenStatusBadgeProps {
  status?: string;
  className?: string;
}

const PenStatusBadge: React.FC<PenStatusBadgeProps> = ({ status, className = '' }) => {
  // Pens without a status are treated as 'empty' elsewhere in the app (sorting, selection)
  const style = STATUS_STYLES[status as PenStatus] || STATUS_STYLES.empty;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${style.badge} ${className}`}
      title={style.help}
      data-testid="pen-status-badge"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {style.label}
    </span>
  );
};

export default PenStatusBadge;
